import React__default, { useEffect, useRef, useCallback, useContext } from 'react';
import { defineMessages, T, L10NContext, t } from '@sm/intl';
import { createUseStyles } from 'react-jss';
import { visuallyHidden } from '@wds/utils';
import { c as classnames, E as ErrorBoundary } from './vendor-a685b0ef.js';
import { sanitizeString } from '@sm/utils';

const createFieldId = questionId => `question-field-${questionId}`;

const createLabelId = questionId => `question-title-${questionId}`;

const createErrorId = questionId => `${questionId}-validation-message`;

const useStyles$8 = createUseStyles(theme => {
  var _a;

  return {
    button: {
      color: theme.nextButton.color,
      backgroundColor: theme.nextButton.backgroundColor,
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontSize: 15,
      border: [1, 'solid', 'transparent'],
      borderRadius: 2,
      padding: [1, 6],
      maxWidth: '100%',
      minWidth: 60,
      minHeight: 40,
      cursor: 'pointer',
      overflowWrap: 'break-word'
    }
  };
}); // This might need to be a prop later once confirmed

const MAX_LENGTH = 20;

const Button = ({
  text = 'OK',
  type,
  ...props
}) => {
  const {
    button
  } = useStyles$8();
  const slicedText = text.length <= MAX_LENGTH ? text : text.slice(0, MAX_LENGTH);
  return (// This is disabled instead of creating a trivial ternary operator
    // eslint-disable-next-line react/button-has-type
    React__default.createElement("button", Object.assign({}, props, {
      className: button,
      type: type
    }), slicedText)
  );
};

const useStyles$7 = createUseStyles({
  visuallyHidden: visuallyHidden
});

const VisuallyHidden = ({
  children,
  element: Element
}) => {
  const {
    visuallyHidden
  } = useStyles$7();
  return React__default.createElement(Element, {
    className: visuallyHidden
  }, children);
};

const useStyles$6 = createUseStyles({
  paddingBox: ({
    padding
  }) => {
    return {
      paddingTop: (padding === null || padding === void 0 ? void 0 : padding.top) || 0,
      paddingBottom: (padding === null || padding === void 0 ? void 0 : padding.bottom) || 0,
      paddingLeft: (padding === null || padding === void 0 ? void 0 : padding.left) || 0,
      paddingRight: (padding === null || padding === void 0 ? void 0 : padding.right) || 0
    };
  }
});

const QuestionSpacing = ({
  padding,
  children
}) => {
  const {
    paddingBox
  } = useStyles$6({
    padding
  });
  return React__default.createElement("div", {
    className: paddingBox
  }, children);
};
/**
 * Hook that calls the provided callback function when the ref element is blurred,
 * only if the next focus is outside the ref element
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useOnFocusLeave(ref, ()=> onLeaveQuestion());
 * <div rf={ref}><input /></div>
 */


function useOnFocusLeave(ref, focusCallback) {
  useEffect(() => {
    const ele = ref === null || ref === void 0 ? void 0 : ref.current;

    function handleFocus(e) {
      if (!(ele === null || ele === void 0 ? void 0 : ele.contains(e.relatedTarget))) {
        focusCallback(ref);
      }
    }

    ele === null || ele === void 0 ? void 0 : ele.addEventListener('focusout', handleFocus);
    return () => {
      ele === null || ele === void 0 ? void 0 : ele.removeEventListener('focusout', handleFocus);
    };
  }, [ref, focusCallback]);
}

const useStyles$5 = createUseStyles({
  footerRow: ({
    hasFooterPadding
  }) => ({
    paddingTop: hasFooterPadding ? 20 : 0
  }),
  containerStyles: ({
    clickShield
  }) => ({
    position: 'relative',
    opacity: clickShield ? '0.5' : '1',
    transition: 'opacity 500ms ease-out',
    pointerEvents: clickShield ? 'none' : 'auto'
  }),
  errorRow: {
    marginBottom: 10
  },
  formReset: {
    border: 0,
    minWidth: 0
  },
  headerRow: {
    marginBottom: 24
  }
});
const COPY$2 = defineMessages({
  QUESTION_TITLE: {
    id: 'ResponseQuestion.QUESTION_TITLE',
    defaultMessage: 'Question Title',
    description: '[Type: Label][Vis: low] - question landmark for screen readers only'
  }
});

const QuestionFieldLayoutTemplate = ({
  footer,
  hasFooterPadding,
  children,
  clickShield,
  error,
  id,
  padding,
  title,
  onSubmit,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onLeave = () => {},
  ...props
}) => {
  const {
    footerRow,
    containerStyles,
    formReset,
    errorRow,
    headerRow
  } = useStyles$5({
    hasFooterPadding,
    clickShield
  });
  const containerRef = useRef(null);
  const handleKeydown = useCallback(event => {
    if (event.key === 'Enter') {
      onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
    }
  }, [onSubmit]);
  const fieldsetRef = useRef(null);
  useOnFocusLeave(fieldsetRef, onLeave);
  useEffect(() => {
    const {
      current: container
    } = containerRef;
    container === null || container === void 0 ? void 0 : container.addEventListener('keydown', handleKeydown);
    return () => {
      return container === null || container === void 0 ? void 0 : container.removeEventListener('keydown', handleKeydown);
    };
  }, [containerRef, handleKeydown]);
  return React__default.createElement("div", Object.assign({
    ref: containerRef,
    id: id
  }, props, {
    className: containerStyles
  }), React__default.createElement(VisuallyHidden, {
    element: "div"
  }, React__default.createElement(T, {
    desc: COPY$2.QUESTION_TITLE
  })), React__default.createElement(QuestionSpacing, {
    padding: padding
  }, error && React__default.createElement("div", {
    className: errorRow
  }, error), React__default.createElement("fieldset", {
    ref: fieldsetRef,
    className: formReset
  }, React__default.createElement("legend", {
    className: headerRow
  }, title), children), footer && React__default.createElement("div", {
    className: footerRow
  }, footer)));
};

createUseStyles(theme => ({
  containerVertical: {
    display: 'initial'
  },
  containerHorizontal: {
    display: 'block'
  },

  /** shared between horizontal and vertical columns */
  column: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },
  columnHorizontal: {},

  /** Horizontal Column that adjust width to content width */
  columnHorizontalAutoAdjust: {
    width: 'auto'
  },
  [`@media (min-width: ${theme.breakpoints.xs.min})`]: {
    containerVertical: {
      display: 'flex'
    },
    containerHorizontal: {
      display: 'flex',
      flexWrap: 'wrap'
    }
  },
  [`@media only screen and (min-width: ${theme.breakpoints.lg.min})`]: {
    columnHorizontal: {
      width: '19%'
    }
  },
  [`@media (min-width: ${theme.breakpoints.sm.min}) and (max-width: ${theme.breakpoints.md.max})`]: {
    columnHorizontal: {
      width: '24%'
    }
  },
  [`@media only screen and (max-width: ${theme.breakpoints.xxs.max})`]: {
    columnHorizontal: {
      width: '100%',
      display: 'block'
    },
    columnHorizontalAutoAdjust: {
      width: '100%',
      display: 'block'
    }
  },
  answerLayoutCell: {
    flex: [0, 0, 'auto'] // fix for IE11 - images leaking out of label container

  }
}));
const textReset = {
  margin: 0
};
const useStyles$4 = createUseStyles(theme => {
  const {
    fontSize,
    ...questionTitleFormatting
  } = theme.questionTitle;
  const questionTitleFontSize = theme.isAccessible ? '22px' : fontSize !== null && fontSize !== void 0 ? fontSize : {};
  return {
    addonContainer: {
      display: 'inline-block',
      margin: [0, 5],
      verticalAlign: 'text-bottom'
    },
    container: {
      position: 'relative'
    },
    questionNumber: ({
      isRTL
    }) => ({
      margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0'
    }),
    questionTitle: ({
      useDefaultFrontSize
    }) => {
      var _a;

      return { ...textReset,
        ...questionTitleFormatting,
        color: ((_a = theme.questionTitle) === null || _a === void 0 ? void 0 : _a.color) || theme.questionColor,
        fontSize: useDefaultFrontSize && !theme.isAccessible ? theme.fontSize.body : questionTitleFontSize,
        lineHeight: 1.25,
        whiteSpace: 'normal',
        wordWrap: 'break-word'
      };
    },
    requiredAsterisk: ({
      isRTL
    }) => ({
      display: 'inline-block',
      margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0'
    })
  };
});
/* eslint-disable @typescript-eslint/naming-convention */

const listSpacing = {
  margin: [16, 0],
  padding: [0, 0, 0, 40]
};
const mediaReset = {
  border: 0,
  maxWidth: '100%'
};
const useStyles$3 = createUseStyles({
  richTextContent: {
    // Typography
    '& a': {
      // Links are not themed by default. Users must select a link color in the editor
      color: 'blue',
      textDecoration: 'underline',
      '&:hover': {
        cursor: 'pointer'
      },
      '&:focus': {
        outline: 'currentColor auto 1px'
      }
    },
    '& ol': { ...listSpacing,
      listStyleType: 'decimal'
    },
    '& ul': { ...listSpacing,
      listStyle: 'disc outside none',
      '& ul li': {
        listStyle: 'circle'
      }
    },
    '& p': {
      margin: [13, 0]
    },
    '& b': {
      fontWeight: 'bold'
    },
    '& strong': {
      fontWeight: 'bold'
    },
    '& em': {
      fontStyle: 'italic'
    },
    '& i': {
      fontStyle: 'italic'
    },
    '& big': {
      fontSize: 'larger'
    },
    '& small': {
      fontSize: 'smaller'
    },
    '& sup': {
      fontSize: '75%',
      lineHeight: 0,
      position: 'relative',
      top: '-0.5em',
      verticalAlign: 'baseline'
    },
    // Media embeds
    '& iframe': mediaReset,
    '& img': { ...mediaReset,
      height: 'auto',
      '-ms-interpolation-mode': 'bicubic'
    }
  }
}); // Support iframe video embeds and their matching attributes

const sanitizeOptions = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'sandbox', 'scrolling']
};

const RichText = ({
  element: Element,
  text
}) => {
  const {
    richTextContent
  } = useStyles$3();
  return React__default.createElement(Element, {
    className: richTextContent,
    dangerouslySetInnerHTML: {
      __html: sanitizeString(text, sanitizeOptions)
    }
  });
};

const QuestionTitle = ({
  element: Element = 'span',
  addon,
  heading,
  id,
  number,
  numbered,
  required,
  useDefaultFrontSize = false,
  ...props
}) => {
  const {
    isRTL
  } = useContext(L10NContext);
  const {
    addonContainer,
    container,
    questionNumber,
    questionTitle,
    requiredAsterisk
  } = useStyles$4({
    useDefaultFrontSize,
    isRTL
  });
  return React__default.createElement(Element, Object.assign({
    id: id,
    className: classnames(container, questionTitle),
    translate: "no"
  }, props), required && React__default.createElement("span", {
    "aria-hidden": true,
    className: requiredAsterisk
  }, "*"), numbered && React__default.createElement("span", {
    className: questionNumber
  }, number, "."), React__default.createElement(RichText, {
    element: "span",
    text: heading
  }), addon && React__default.createElement("span", {
    className: addonContainer
  }, addon));
};

const useStyles$2 = createUseStyles(theme => {
  var _a;

  return {
    icon: {
      backgroundClip: 'padding-box',
      backgroundColor: theme.primaryAccentColor,
      borderRadius: 3,
      color: theme.secondaryAccentColor,
      fontSize: theme.fontSize.body,
      padding: [0, 5]
    },
    iconWrapper: {
      marginRight: 5
    },
    validationMessage: {
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      lineHeight: 1.5,
      color: theme.questionBody.color,
      fontWeight: theme.fontWeight.light,
      fontSize: theme.fontSize.body
    }
  };
});

const ValidationMessage = ({
  children,
  id,
  ...props
}) => {
  const {
    icon,
    iconWrapper,
    validationMessage
  } = useStyles$2();
  return React__default.createElement("h4", Object.assign({
    className: validationMessage,
    "aria-live": "polite",
    id: id
  }, props), React__default.createElement("span", {
    className: iconWrapper
  }, React__default.createElement("span", {
    "aria-hidden": "true",
    className: icon
  }, "!")), React__default.createElement("span", {
    translate: "no"
  }, children));
};

const useStyles$1 = createUseStyles(theme => {
  var _a;

  return {
    textareaStyles: {
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontSize: '18px',
      lineHeight: '1.15em',
      padding: '6px 60px 6px 6px',
      maxWidth: '100%',
      color: theme.questionBody.color,
      outlineColor: theme.primaryAccentColor,
      transition: 'all 0.1s linear',
      verticalAlign: 'top',
      textSizeAdjust: 'auto',
      ...(theme.isAccessible ? {
        fontWeight: 500,
        opacity: 1,
        border: `1px solid ${theme.answerColor}`,
        backgroundColor: '#fff'
      } : {
        fontWeight: 300,
        backgroundColor: theme.input.bgColor,
        border: '0'
      }),
      '&:focus': {
        borderColor: theme.input.activeColor,
        borderRadius: '2px'
      },
      '&:focus, &:hover': theme.isAccessible ? {
        // should be there but was broken
        // https://code.corp.surveymonkey.com/devmonkeys/smlib.surveytemplates/blob/main/smlib/surveytemplates/themes_v3/base_css.jinja2#L1094-L1097
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 7)'
      } : {},
      '&:disabled, &:read-only': {
        boxShadow: 'none',
        ...(theme.isAccessible ? {} : {
          borderColor: 'transparent',
          opacity: 0.5
        })
      }
    },
    [`@media only screen and (max-width: ${theme.breakpoints.md.max})`]: {
      textareaStyles: {
        fontSize: '16px'
      }
    }
  };
}); // Custom textarea component for Response.

const TextArea = ({
  placeholder,
  disabled,
  rows,
  cols,
  id,
  name,
  defaultValue,
  labelId,
  errorId,
  required = false,
  readOnly = false,
  onChange,
  onBlur,
  onFocus
}) => {
  const {
    textareaStyles
  } = useStyles$1();
  return React__default.createElement("textarea", {
    className: textareaStyles,
    placeholder: placeholder,
    disabled: disabled,
    rows: rows,
    cols: cols,
    id: id,
    name: name,
    defaultValue: defaultValue,
    "aria-labelledby": labelId,
    "aria-describedby": errorId,
    "aria-required": required,
    onChange: onChange,
    onBlur: onBlur,
    onFocus: onFocus,
    spellCheck: true,
    readOnly: readOnly
  });
};

const useStyles = createUseStyles(theme => {
  var _a;

  return {
    inputStyles: {
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontSize: '18px',
      lineHeight: '1.5em',
      height: ({
        autoHeight
      }) => autoHeight ? {} : '50px',
      padding: '6px',
      maxWidth: '100%',
      color: theme.questionBody.color,
      transition: 'all 0.1s linear',
      ...(theme.isAccessible ? {
        fontWeight: 500,
        opacity: 1,
        border: theme.isAccessible ? `1px solid ${theme.answerColor}` : '0',
        backgroundColor: '#fff'
      } : {
        fontWeight: 300,
        backgroundColor: theme.input.bgColor,
        border: '0'
      }),
      '&:focus': {
        outline: 'none',
        borderColor: theme.input.activeColor,
        borderRadius: '2px'
      },
      '&:focus, &:hover': { ...(theme.isAccessible ? {
          // should be there but was broken
          // https://code.corp.surveymonkey.com/devmonkeys/smlib.surveytemplates/blob/main/smlib/surveytemplates/themes_v3/base_css.jinja2#L1094-L1097
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 7)'
        } : {})
      },
      '&:disabled, &:read-only': {
        boxShadow: 'none',
        ...(theme.isAccessible ? {} : {
          borderColor: 'transparent',
          opacity: 0.5
        })
      }
    },
    [`@media only screen and (max-width: ${theme.breakpoints.md.max})`]: {
      inputStyles: {
        fontSize: '16px'
      }
    }
  };
});

const TextInput = ({
  disabled,
  errorId,
  id,
  labelId,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  required = false,
  readOnly = false,
  autoHeight = false,
  value,
  size = 50
}) => {
  const {
    inputStyles
  } = useStyles({
    autoHeight
  });
  return React__default.createElement("input", {
    type: "text",
    className: inputStyles,
    placeholder: placeholder,
    disabled: disabled,
    size: size,
    id: id,
    name: name,
    value: value,
    onChange: onChange,
    onBlur: onBlur,
    onFocus: onFocus,
    "aria-labelledby": labelId,
    "aria-describedby": errorId,
    "aria-required": required,
    "aria-invalid": !!errorId,
    readOnly: readOnly
  });
};

const ErrorFallback = ({
  error
}) => {
  // TODO: temporary fallback ui (RAWR-773)
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("h1", null, "Sorry.. there was an error"), React__default.createElement("p", null, error.message));
};

const withErrorBoundary = Component => {
  return props => React__default.createElement(ErrorBoundary, {
    FallbackComponent: ErrorFallback
  }, React__default.createElement(Component, Object.assign({}, props)));
};

const COPY$1 = defineMessages({
  OK_LABEL: {
    id: 'SingleTextBox.OK_LABEL',
    defaultMessage: 'OK',
    description: '[Type: button][Vis: high] - question ok label'
  }
});

const QuestionField = ({
  active,
  disabled,
  error,
  title,
  onSubmit,
  onChange,
  onLeave,
  padding = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  questionId,
  questionNumber,
  required,
  survey: {
    asteriskEnabled,
    numberingEnabled
  },
  value,
  okLabel = t(COPY$1.OK_LABEL),
  variant,
  ...conditionalProps
}) => {
  const titleId = createLabelId(questionId);
  const errorId = createErrorId(questionId);
  const fieldId = createFieldId(questionId);
  const isAutoScroll = variant === 'auto-scroll';
  const showClickShield = isAutoScroll && !active; // Requires more conditional logic on integration with RAWR-109 form manager

  const showButton = isAutoScroll && active && 'showOkButton' in conditionalProps && conditionalProps.showOkButton;
  return React__default.createElement("div", null, React__default.createElement(QuestionFieldLayoutTemplate, {
    footer: showButton && React__default.createElement(Button, {
      text: okLabel,
      type: "button",
      onClick: onSubmit
    }),
    hasFooterPadding: true,
    clickShield: showClickShield,
    error: error && React__default.createElement(ValidationMessage, {
      id: errorId
    }, error),
    onLeave: onLeave,
    id: fieldId,
    padding: padding,
    title: React__default.createElement(QuestionTitle, {
      heading: title,
      id: titleId,
      number: questionNumber,
      numbered: numberingEnabled,
      required: asteriskEnabled && required
    })
  }, React__default.createElement(TextInput, {
    value: value,
    disabled: disabled,
    errorId: error && errorId,
    id: questionId,
    labelId: titleId,
    name: questionId,
    required: required,
    onChange: event => onChange === null || onChange === void 0 ? void 0 : onChange(event.target.value)
  })));
};

var SingleTextbox = withErrorBoundary(QuestionField);
const COPY = defineMessages({
  OK_LABEL: {
    id: 'CommentBox.OK_LABEL',
    defaultMessage: 'OK',
    description: '[Type: button][Vis: high] - question ok label'
  }
});

const CommentBox = ({
  active,
  cols = 50,
  disabled,
  error,
  title,
  onSubmit,
  onChange,
  onLeave,
  padding = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  questionId,
  questionNumber,
  required,
  rows = 3,
  survey: {
    asteriskEnabled,
    numberingEnabled
  },
  value: defaultValue,
  okLabel = t(COPY.OK_LABEL),
  variant,
  ...conditionalProps
}) => {
  const titleId = createLabelId(questionId);
  const errorId = createErrorId(questionId);
  const fieldId = createFieldId(questionId);
  const isAutoScroll = variant === 'auto-scroll';
  const showClickShield = isAutoScroll && !active; // // Requires more conditional logic on integration with RAWR-109 form manager

  const showButton = isAutoScroll && active && 'showOkButton' in conditionalProps && conditionalProps.showOkButton;
  return React__default.createElement(QuestionFieldLayoutTemplate, {
    footer: showButton && React__default.createElement(Button, {
      text: okLabel,
      type: "button",
      onClick: onSubmit
    }),
    hasFooterPadding: true,
    clickShield: showClickShield,
    error: error && React__default.createElement(ValidationMessage, {
      id: errorId
    }, error),
    onLeave: onLeave,
    id: fieldId,
    padding: padding,
    title: React__default.createElement(QuestionTitle, {
      heading: title,
      id: titleId,
      number: questionNumber,
      numbered: numberingEnabled,
      required: asteriskEnabled && required
    })
  }, React__default.createElement(TextArea, {
    cols: cols,
    defaultValue: defaultValue,
    disabled: disabled,
    errorId: error && errorId,
    id: questionId,
    labelId: titleId,
    name: questionId,
    required: required,
    rows: rows,
    onChange: event => onChange === null || onChange === void 0 ? void 0 : onChange(event.target.value)
  }));
};

var CommentBox$1 = withErrorBoundary(CommentBox);
export { CommentBox$1 as CommentBox, SingleTextbox };